import { useState } from "react";
import { useDataStore } from "@store/user_org";
import api from "@services/api/client";
import { showNotification } from "context/notification.service";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import { Text1, Text2 } from "@components/ui/texts/Texts";

const DEFAULT_MESSAGES = {
  r1_whatsapp:
    "Bonjour {client_name}, votre devis pour {vehicle_info} est en attente de validation. Consultez-le ici : {devis_url}",
  r1_email_subject: "Rappel : votre devis est en attente — Demande #{ordre_num}",
  r2_whatsapp:
    "Bonjour {client_name}, n'oubliez pas de valider votre devis pour {vehicle_info}. Lien : {devis_url}",
  r2_email_subject: "Relance : votre devis attend votre validation — Demande #{ordre_num}",
  r3_whatsapp:
    "Bonjour {client_name}, dernier rappel pour valider votre devis ({vehicle_info}). Sans validation, votre rendez-vous sera annulé automatiquement. Lien : {devis_url}",
  r3_email_subject:
    "Dernier rappel : validez votre devis avant annulation — Demande #{ordre_num}",
  cancel_whatsapp:
    "Bonjour {client_name}, votre rendez-vous pour {vehicle_info} a été annulé car le devis n'a pas été validé dans les délais.",
  cancel_email_subject: "Rendez-vous annulé — Demande #{ordre_num}",
};

const VARIABLES = [
  "{client_name}",
  "{vehicle_info}",
  "{devis_url}",
  "{org_name}",
  "{ordre_num}",
  "{reception_date}",
];

type Config = {
  enabled: boolean;
  r1_delay_hours: number;
  r2_delay_hours: number;
  r3_before_reception_hours: number;
  cancel_before_reception_hours: number;
  min_gap_hours: number;
  messages: Record<string, string>;
};

export function DevisReminders() {
  const orgData = useDataStore((s) => s.orgData);
  const orgId = orgData?.id;
  const fetchOrgData = useDataStore((s) => s.fetchOrgData);

  const existing: any = orgData?.config?.devis_reminders || {};

  const [config, setConfig] = useState<Config>({
    enabled: existing.enabled !== false,
    r1_delay_hours: existing.r1_delay_hours ?? 24,
    r2_delay_hours: existing.r2_delay_hours ?? 48,
    r3_before_reception_hours: existing.r3_before_reception_hours ?? 48,
    cancel_before_reception_hours: existing.cancel_before_reception_hours ?? 42,
    min_gap_hours: existing.min_gap_hours ?? 12,
    messages: { ...DEFAULT_MESSAGES, ...(existing.messages || {}) },
  });

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);
    try {
      await api.patch(`/organizations/${orgId}`, {
        config: { ...orgData?.config, devis_reminders: config },
      });
      showNotification({ type: "success", message: "Configuration sauvegardée" });
      fetchOrgData(orgId);
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig({
      enabled: true,
      r1_delay_hours: 24,
      r2_delay_hours: 48,
      r3_before_reception_hours: 48,
      cancel_before_reception_hours: 42,
      min_gap_hours: 12,
      messages: { ...DEFAULT_MESSAGES },
    });
  };

  const updateNumber = (key: keyof Config, value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num > 0) {
      setConfig((c) => ({ ...c, [key]: num }));
    }
  };

  const updateMessage = (key: string, value: string) => {
    setConfig((c) => ({ ...c, messages: { ...c.messages, [key]: value } }));
  };

  return (
    <div className="flex flex-col gap-6 p-6 max-w-2xl">
      <Text1>Rappels devis</Text1>

      <SwitchBrick
        checked={config.enabled}
        onChange={() => setConfig((c) => ({ ...c, enabled: !c.enabled }))}
        title="Rappels automatiques"
        description="Activer les rappels automatiques de validation des devis"
      />

      {config.enabled && (
        <>
          <div className="border rounded-xl p-4 shadow-sm">
            <Text2>Planification</Text2>
            <div className="grid grid-cols-1 gap-3 mt-3">
              {[
                { key: "r1_delay_hours" as const, label: "Rappel 1 : heures après envoi" },
                { key: "r2_delay_hours" as const, label: "Rappel 2 : heures après envoi" },
                { key: "r3_before_reception_hours" as const, label: "Dernier rappel : heures avant réception" },
                { key: "cancel_before_reception_hours" as const, label: "Annulation auto : heures avant réception" },
                { key: "min_gap_hours" as const, label: "Écart minimum : heures entre rappels" },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-3">
                  <input
                    type="number"
                    min={1}
                    value={config[key]}
                    onChange={(e) => updateNumber(key, e.target.value)}
                    className="w-16 border rounded-xl px-2 py-2 text-h3 text-center shadow-sm focus:ring-4 focus:ring-neutral-300 outline-none transition"
                  />
                  <span className="text-h3 text-neutral-500">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border rounded-xl p-4 shadow-sm">
            <Text2>Messages WhatsApp</Text2>
            <div className="flex flex-col gap-4 mt-3">
              {[
                { key: "r1_whatsapp", label: "Rappel 1" },
                { key: "r2_whatsapp", label: "Rappel 2" },
                { key: "r3_whatsapp", label: "Dernier rappel" },
                { key: "cancel_whatsapp", label: "Notification annulation" },
              ].map(({ key, label }) => (
                <AreaTextField
                  key={key}
                  label={label}
                  value={config.messages[key] || ""}
                  onChange={(v) => updateMessage(key, v)}
                  rows={3}
                />
              ))}
            </div>
          </div>

          <div className="border rounded-xl p-4 shadow-sm">
            <Text2>Objets email</Text2>
            <div className="flex flex-col gap-3 mt-3">
              {[
                { key: "r1_email_subject", label: "Rappel 1" },
                { key: "r2_email_subject", label: "Rappel 2" },
                { key: "r3_email_subject", label: "Dernier rappel" },
                { key: "cancel_email_subject", label: "Notification annulation" },
              ].map(({ key, label }) => (
                <TextField
                  key={key}
                  label={label}
                  value={config.messages[key] || ""}
                  onChange={(v) => updateMessage(key, v)}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="text-h4 text-neutral-400">Variables disponibles :</span>
            {VARIABLES.map((v) => (
              <span key={v} className="text-h4 bg-neutral-100 px-2 py-0.5 rounded font-mono">
                {v}
              </span>
            ))}
          </div>
        </>
      )}

      <div className="flex justify-end gap-3 border-t pt-4">
        <SecondaryButton label="Réinitialiser" onClick={handleReset} variant="small" />
        <Button
          label={saving ? "Enregistrement..." : "Enregistrer"}
          onClick={handleSave}
          disabled={saving}
          loading={saving}
          variant="small"
        />
      </div>
    </div>
  );
}
